import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateInput } from "@/components/DateInput";
import { MultiSelect } from "@/components/sales/MultiSelect";

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>;

/** Create (with people) or revise (fields + reply to the reviewers) a task. */
export const TaskForm = ({ people, initial, revise = false, busy, onSubmit }) => {
  const [v, setV] = useState({ title: "", description: "", priority: "Medium", due_date: "", assignee: "", reviewers: [], reply: "", ...initial });
  const set = (p) => setV((s) => ({ ...s, ...p }));
  const ready = v.title.trim().length >= 2 && v.due_date && (revise || (v.assignee && v.reviewers.length));
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (ready) onSubmit(v); }} data-testid="task-form">
      <F label="Title"><Input value={v.title} maxLength={160} onChange={(e) => set({ title: e.target.value })} data-testid="task-title-input" /></F>
      <F label="Description"><Textarea rows={4} value={v.description} maxLength={5000} onChange={(e) => set({ description: e.target.value })} data-testid="task-description-input" /></F>
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Priority">
          <Select value={v.priority} onValueChange={(x) => set({ priority: x })}>
            <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["High", "Medium", "Low"].map((p) => <SelectItem key={p} value={p} data-testid={`task-priority-${p}`}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Due date"><DateInput value={v.due_date} onChange={(x) => set({ due_date: x })} className="w-full" testid="task-due-input" /></F>
      </div>
      {!revise && (
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Manager (one)">
            <Select value={v.assignee} onValueChange={(x) => set({ assignee: x })}>
              <SelectTrigger data-testid="task-manager-select"><SelectValue placeholder="Choose a Manager" /></SelectTrigger>
              <SelectContent>{people.managers.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`task-manager-${p.id}`}>{p.name} · {p.role_label}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Division Heads (one or more)">
            <MultiSelect label="Division Heads" staticOptions={people.reviewers.map((p) => ({ value: p.id, label: p.name }))} value={v.reviewers} onChange={(x) => set({ reviewers: x })} testid="task-reviewers-select" />
          </F>
        </div>
      )}
      {revise && <F label="Reply to the Division Heads (optional)"><Textarea rows={3} value={v.reply} maxLength={2000} onChange={(e) => set({ reply: e.target.value })} data-testid="task-reply-input" /></F>}
      {!revise && <p className="text-xs text-slate-500">The task goes to the Division Heads first and reaches the Manager only after all of them endorse it.</p>}
      <Button type="submit" disabled={!ready || busy} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="task-submit-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : revise ? "Send back for review" : "Create task"}
      </Button>
    </form>
  );
};
